import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {FeedComment} from '../../../../services/socialFeedApi';
import {
  formatRelativeTime,
  getAssetUrl,
  getDisplayInitial,
  getProfilePath,
} from '../utils';
import HomeModalShell from './HomeModalShell';
import styles from '../Home.module.css';
import {HomeCommentTarget} from '../hooks/useHomeComments';

interface CommentsModalProps {
  target: HomeCommentTarget | null;
  isOpen: boolean;
  comments: FeedComment[];
  loading: boolean;
  error: string | null;
  draft: string;
  validationError: string | null;
  submitError: string | null;
  submitting: boolean;
  deletingCommentIds: Record<string, boolean>;
  currentUserId?: string;
  onDraftChange: (value: string) => void;
  onRetry: () => void;
  onClose: () => void;
  onSubmit: () => void;
  onDeleteComment: (commentId: string) => void;
}

interface CommentAvatarProps {
  name: string;
  profilePicture?: string;
}

const CommentAvatar: React.FC<CommentAvatarProps> = ({name, profilePicture}) => {
  const [hasImageError, setHasImageError] = useState(false);
  const avatarUrl = getAssetUrl(profilePicture);
  const shouldRenderImage = Boolean(avatarUrl) && !hasImageError;

  if (shouldRenderImage) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={styles.commentsAvatarImage}
        onError={() => setHasImageError(true)}
      />
    );
  }

  return (
    <div className={styles.commentsAvatarFallback}>
      {getDisplayInitial(name)}
    </div>
  );
};

const CommentsModal: React.FC<CommentsModalProps> = ({
  target,
  isOpen,
  comments,
  loading,
  error,
  draft,
  validationError,
  submitError,
  submitting,
  deletingCommentIds,
  currentUserId,
  onDraftChange,
  onRetry,
  onClose,
  onSubmit,
  onDeleteComment,
}) => {
  if (!isOpen || !target) {
    return null;
  }

  return (
    <HomeModalShell
      backdropClassName={styles.commentsBackdrop}
      panelClassName={`${styles.modalCard} ${styles.commentsModalCard}`}
      ariaLabelledBy="home-comments-modal-title"
      ariaDescribedBy="home-comments-modal-description"
      onClose={onClose}
      closeDisabled={submitting}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="home-comments-modal-title" className={styles.modalTitle}>Comments</h2>
          <p id="home-comments-modal-description" className={styles.modalDescription}>
            <span className={styles.commentsTargetTitle}>{target.title}</span>
            {target.subtitle ? ` · ${target.subtitle}` : ''}
            {` · ${target.commentCount} comment${target.commentCount === 1 ? '' : 's'}`}
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          disabled={submitting}
          aria-label="Close comments"
        >
          ×
        </button>
      </div>

      <div className={styles.commentsBody}>
        {loading ? (
          <div className={styles.commentsStateCard}>
            <p className={styles.feedStateText}>Loading comments...</p>
          </div>
        ) : null}

        {!loading && error ? (
          <div className={styles.commentsStateCard}>
            <p className={styles.cardErrorTextSpaced}>{error}</p>
            <button type="button" className={styles.primaryAction} onClick={onRetry}>
              Retry
            </button>
          </div>
        ) : null}

        {!loading && !error && comments.length === 0 ? (
          <div className={styles.commentsStateCard}>
            <h4 className={styles.feedStateTitle}>No comments yet</h4>
            <p className={styles.feedStateText}>Start the conversation by leaving the first comment.</p>
          </div>
        ) : null}

        {!loading && !error && comments.length > 0 ? (
          <div className={styles.commentsList}>
            {comments.map((comment) => {
              const isCurrentUserComment = comment.author.id === currentUserId;
              const isDeleting = Boolean(deletingCommentIds[comment.id]);
              const authorName = comment.author.name || 'User';
              const authorProfilePath = getProfilePath(comment.author.id);

              return (
                <article key={comment.id} className={styles.commentCard}>
                  <div className={styles.commentAvatar}>
                    <CommentAvatar
                      name={authorName}
                      profilePicture={comment.author.profilePicture}
                    />
                  </div>
                  <div className={styles.commentContent}>
                    <div className={styles.commentHeader}>
                      <div className={styles.commentMeta}>
                        <span className={styles.commentAuthor}>
                          {authorProfilePath ? (
                            <Link to={authorProfilePath} className={styles.commentAuthorLink}>
                              {authorName}
                            </Link>
                          ) : authorName}
                        </span>
                        <span className={styles.commentTimestamp}>{formatRelativeTime(comment.createdAt)}</span>
                      </div>
                      {isCurrentUserComment ? (
                        <button
                          type="button"
                          className={styles.commentDeleteButton}
                          disabled={isDeleting}
                          onClick={() => onDeleteComment(comment.id)}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                      ) : null}
                    </div>
                    <p className={styles.commentText}>{comment.content}</p>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className={styles.commentsComposer}>
        <label className={styles.modalLabel} htmlFor="home-comments-draft">
          Add a comment
        </label>
        <textarea
          id="home-comments-draft"
          className={`${styles.modalTextarea} ${styles.commentsTextarea}`}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          placeholder={target.type === 'post' ? 'Share your thoughts on this post...' : 'Share your thoughts on this reel...'}
          maxLength={2000}
          disabled={submitting}
        />

        {validationError ? (
          <div className={`${styles.modalMessage} ${styles.modalValidationMessage}`}>
            {validationError}
          </div>
        ) : null}

        {submitError ? (
          <div className={`${styles.modalMessage} ${styles.modalErrorMessage}`}>
            {submitError}
          </div>
        ) : null}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
            disabled={submitting}
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            disabled={submitting}
            onClick={onSubmit}
          >
            {submitting ? 'Posting...' : 'Post comment'}
          </button>
        </div>
      </div>
    </HomeModalShell>
  );
};

export default React.memo(CommentsModal);
